import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import type { UpdateProfileRequest } from '../types/profile';
import { toast } from '../hooks/use-toast';

export function useCustomerProfile() {
  const queryClient = useQueryClient();

  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: async () => {
      const response = await apiService.getCustomerProfile();
      
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch profile'));
      }
      return response.data!;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      const response = await apiService.updateCustomerProfile(data);
      
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update profile'));
      }
      return response.data!;
    },
    onSuccess: (data) => {
      // Update the cache with the new profile data
      queryClient.setQueryData(['customer-profile'], data);
      
      toast({
        title: 'Profile updated',
        description: 'Your profile has been successfully updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Failed to update profile',
        variant: 'destructive',
      });
    },
  });

  return {
    profile,
    isLoading,
    isError,
    error,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,
  };
}